import { useQuery } from "@tanstack/react-query";
import { getClientConfig } from "@/api/endpoints";

export interface FeatureFlags {
  throughput: boolean;
  /** Operations dashboard tab + /api/operations/* (per-user flag, see Features:Operations). */
  operations: boolean;
  /** "Ask Claude" on the Operations tab (Features:OperationsAssistant; also needs operations). */
  operationsAssistant: boolean;
}

const DEFAULTS: FeatureFlags = { throughput: false, operations: false, operationsAssistant: false };

export interface FeatureFlagsState {
  flags: FeatureFlags;
  /** True once the config call has settled (success or failure); false while it is still in flight. */
  loaded: boolean;
}

/**
 * Server-driven feature flags from `GET /api/config`, with whether they have arrived yet. Gated UI
 * can simply hide while loading, but a decision that must not be taken on defaults (e.g. which
 * screen to land on) waits for `loaded`. A failed config call counts as loaded with everything off.
 */
export function useFeatureFlagsState(): FeatureFlagsState {
  const { data, isSuccess, isError } = useQuery({
    queryKey: ["config"],
    queryFn: getClientConfig,
    staleTime: Infinity,
  });
  return { flags: data?.features ?? DEFAULTS, loaded: isSuccess || isError };
}

/**
 * Server-driven feature flags from `GET /api/config`. Defaults to everything off until the config
 * resolves, so gated UI (e.g. the Throughput tab) stays hidden unless the API opts in.
 */
export function useFeatureFlags(): FeatureFlags {
  return useFeatureFlagsState().flags;
}
